// Screen Controller - Manages screen swapping and event delegation
import { store } from '../app/store.js';
import { Email } from '../domain/valueObjects.js';
import Alpine from 'alpinejs';
import { formatPrice } from '../utils/currency.js';
import { CoinPiles } from '../components/CoinPiles.js';
import {
  initFaceLandmarksCamera,
  type FaceLandmarksController
} from '../components/face-landmarks/adapter.js';

export class ScreenController {
  private root: HTMLElement;
  private currentScreenElement: HTMLElement | null = null;
  private countdownTimerId: number | null = null;
  private currentScreen: any = null;
  private voucherTimerIds: Map<string, number> = new Map();
  private coinPiles: CoinPiles | null = null;
  private faceLandmarksController: FaceLandmarksController | null = null;

  constructor(rootElement: HTMLElement) {
    this.root = rootElement;
    this.currentScreenElement = null;
    this.setupEventDelegation();
  }

  swapScreen(screen) {
    const template = document.getElementById(screen.template);
    if (!template) {
      console.error(`Template ${screen.template} not found`);
      return;
    }

    // Clean up camera when leaving camera screen
    this.cleanupCamera();
    document.body.classList.remove('camera-active');

    // Store current screen
    this.currentScreen = screen;
    console.log('Swapping to screen:', screen);

    // Clone template content
    const screenContent = template.content.cloneNode(true);

    // Clear current content with transition
    this.root.style.opacity = '0';

    setTimeout(() => {
      // Replace content
      this.root.replaceChildren(screenContent);
      this.currentScreenElement = this.root.querySelector('[data-screen]');

      // Bind data if screen has data field
      if (screen.dataField) {
        this.bindScreenData(screen);
      }

      // Special handling for certain screens
      this.handleSpecialScreens(screen);

      // Fade in new content
      this.root.style.opacity = '1';

      // Focus for accessibility
      this.root.focus();

      // Dispatch screen loaded event
      document.dispatchEvent(new CustomEvent('screen:loaded', {
        detail: { screen }
      }));
    }, 150); // Match with CSS transition duration
  }

  setupEventDelegation() {
    // Handle clicks on action buttons
    this.root.addEventListener('click', (event) => {
      const target = event.target.closest('[data-action]');
      if (!target) return;

      const action = target.getAttribute('data-action');
      const value = target.getAttribute('data-value');

      this.handleAction(action, value, target, event);
    });

    // Handle input changes
    this.root.addEventListener('input', (event) => {
      const target = event.target;
      if (target.hasAttribute('data-bind')) {
        const field = target.getAttribute('data-bind');
        store.set(field, target.value);

        // Special validation for email field
        if (field === 'email') {
          this.validateEmailInput(target);
        }
      }
    });
  }

  handleAction(action, value, element, event) {
    event.preventDefault();

    switch (action) {
      case 'next':
        this.validateAndProceed();
        break;

      case 'back':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'back' } }));
        break;

      case 'skip':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'select':
        this.handleSelection(value, element);
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'toggle':
        this.handleToggle(value, element);
        break;

      case 'capture-selfie':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'subscribe':
        this.openCheckout();
        break;

      case 'download-app':
        this.downloadApp();
        break;

      default:
        // Dispatch custom event for other actions
        document.dispatchEvent(new CustomEvent(`action:${action}`, {
          detail: { value, element }
        }));
    }
  }

  handleSelection(value, element) {
    // Remove selected class from siblings
    const parent = element.parentElement;
    parent.querySelectorAll('.option-card').forEach(card => {
      card.classList.remove('selected');
    });

    // Add selected class
    element.classList.add('selected');

    // Store value
    const screen = this.getCurrentScreen();
    if (screen?.dataField) {
      store.set(screen.dataField, value);
    }
  }

  handleToggle(value, element) {
    // Handle exclusive options
    if (element.classList.contains('exclusive')) {
      // If selecting "none", deselect all others
      const parent = element.parentElement;
      if (!element.classList.contains('active')) {
        parent.querySelectorAll('.selectable').forEach(card => {
          card.classList.remove('active');
        });
      }
    } else {
      // Deselect "none" if selecting other options
      const parent = element.parentElement;
      const noneOption = parent.querySelector('.exclusive');
      if (noneOption && noneOption.classList.contains('active')) {
        noneOption.classList.remove('active');
      }
    }

    // Toggle active class
    element.classList.toggle('active');

    // Collect all active values
    const parent = element.parentElement;
    const activeValues = Array.from(parent.querySelectorAll('.selectable.active'))
      .map(el => el.getAttribute('data-value'));

    // Store values
    const screen = this.getCurrentScreen();
    if (screen?.dataField) {
      store.set(screen.dataField, activeValues);
    }
  }

  validateAndProceed() {
    const screen = this.getCurrentScreen();

    // Check required fields
    if (screen?.required) {
      const input = this.root.querySelector(`[data-bind="${screen.dataField}"]`);
      if (!input || !input.value) {
        this.showValidationError('This field is required');
        return;
      }

      // Email validation using domain layer
      if (screen.validation === 'email') {
        if (!Email.isValid(input.value.trim())) {
          this.showValidationError('Please enter a valid email address');
          return;
        }

        // Store the validated email as lowercase (Email value object behavior)
        store.set(screen.dataField, input.value.trim().toLowerCase());
      }
    }

    // Proceed to next screen
    document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
  }

  showValidationError(message) {
    const validation = this.root.querySelector('.input-validation');
    if (validation) {
      validation.textContent = message;
      validation.classList.add('error');
    }
  }

  bindScreenData(screen) {
    const storedValue = store.get(screen.dataField);
    if (!storedValue) return;

    // For text inputs
    const input = this.root.querySelector(`[data-bind="${screen.dataField}"]`);
    if (input) {
      input.value = storedValue;
    }

    // For single select options
    if (!screen.multiSelect && typeof storedValue === 'string') {
      const option = this.root.querySelector(`[data-value="${storedValue}"]`);
      if (option) {
        option.classList.add('selected');
      }
    }

    // For multi-select options
    if (screen.multiSelect && Array.isArray(storedValue)) {
      storedValue.forEach(value => {
        const option = this.root.querySelector(`[data-value="${value}"]`);
        if (option) {
          option.classList.add('active');
        }
      });
    }
  }

  handleSpecialScreens(screen) {
    switch (screen.id) {
      case 'processing':
        this.startProgressAnimation(screen.duration || 8000);
        break;

      case 'analysis':
        this.displayAnalysisScreen();
        this.startProgressAnimation(screen.duration || 8000);
        break;

      case 'social-proof':
        this.updateSocialProofText();
        break;

      case 'email':
        this.setupEmailValidation();
        break;

      case 'camera':
        document.body.classList.add('camera-active');
        this.initializeCamera();
        break;

      case 'results':
        this.displayResults();
        break;

      case 'paywall':
        this.initializePaywall();
        break;

      case 'success':
        this.showSuccessAnimation();
        break;

      case 'prediction':
        this.initializePredictionAnimations();
        break;

      case 'subscription':
        this.initializePaywall();
        break;

      case 'smart-investment':
        this.initializeCoinPiles();
        break;
    }
  }

  startProgressAnimation(duration) {
    const stages = [
      { text: 'Analyzing your skin profile...', progress: 25 },
      { text: 'Reviewing your lifestyle factors...', progress: 50 },
      { text: 'Comparing with thousands of success stories...', progress: 75 },
      { text: 'Generating your personalized plan...', progress: 100 }
    ];

    let currentStage = 0;
    const interval = duration / stages.length;

    // Store the current screen element to check if we're still on the same screen
    const initialScreenElement = this.currentScreenElement;

    const updateStage = () => {
      // Check if we're still on the same screen
      if (this.currentScreenElement !== initialScreenElement) {
        // User navigated away, stop the animation
        return;
      }

      if (currentStage >= stages.length) {
        // Double-check we're still on the same screen before navigating
        if (this.currentScreenElement === initialScreenElement) {
          setTimeout(() => {
            // Final check before navigation
            if (this.currentScreenElement === initialScreenElement) {
              document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
            }
          }, 500);
        }
        return;
      }

      const stage = stages[currentStage];
      const textElement = this.root.querySelector('#processing-text, #analysis-text');
      const progressBar = this.root.querySelector('#analysis-bar');
      const progressPercent = this.root.querySelector('#progress-percentage');
      const progressRing = this.root.querySelector('#analysis-progress');

      if (textElement) textElement.textContent = stage.text;
      if (progressBar) progressBar.style.width = `${stage.progress}%`;
      if (progressPercent) progressPercent.textContent = `${stage.progress}%`;
      if (progressRing) {
        const offset = 283 - (283 * stage.progress / 100);
        progressRing.style.strokeDashoffset = offset;
      }

      currentStage++;
      setTimeout(updateStage, interval);
    };

    updateStage();
  }

  updateSocialProofText() {
    const goal = store.get('primary_goal');
    const goalText = this.root.querySelector('#user-goal-text');

    if (goalText && goal) {
      const goalMap = {
        'clear-acne': 'clear breakouts and prevent acne',
        'reduce-aging': 'reduce fine lines and wrinkles',
        'even-tone': 'even out skin tone',
        'hydrate': 'hydrate and nourish skin',
        'calm-redness': 'calm redness and sensitivity',
        'skip': 'skin improvement'
      };

      goalText.textContent = goalMap[goal] || 'similar goals';
    }
  }

  setupEmailValidation() {
    const emailInput = this.root.querySelector('#email-input');
    const submitButton = this.root.querySelector('#email-submit');

    if (emailInput && submitButton) {
      // Initially disable the button
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');

      // Validate existing value if any
      if (emailInput.value) {
        this.validateEmailInput(emailInput);
      }
    }
  }

  validateEmailInput(input) {
    const submitButton = this.root.querySelector('#email-submit');
    const validation = this.root.querySelector('#email-validation');

    if (!submitButton) return;

    const value = input.value.trim();

    // Clear previous validation
    if (validation) {
      validation.textContent = '';
      validation.classList.remove('error');
    }

    if (!value) {
      // Empty email
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');
    } else if (!Email.isValid(value)) {
      // Invalid email - using domain layer validation
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');
      if (validation) {
        validation.textContent = 'Please enter a valid email address';
        validation.classList.add('error');
      }
    } else {
      // Valid email
      submitButton.disabled = false;
      submitButton.classList.remove('btn-disabled');
    }
  }

  async initializeCamera() {
    // Clean up any existing controller
    if (this.faceLandmarksController) {
      this.faceLandmarksController.dispose();
      this.faceLandmarksController = null;
    }

    const container = this.root.querySelector('#face-landmarks-container');
    const loadingSpinner = this.root.querySelector('#camera-loading');

    if (!container) {
      console.error('Face landmarks container not found');
      return;
    }

    // Show loading spinner
    if (loadingSpinner) {
      loadingSpinner.classList.remove('hidden');
    }

    // Listen for completion event to show continue button
    const continueBtn = this.root.querySelector('#camera-continue-btn');
    const handleComplete = () => {
      if (continueBtn) {
        continueBtn.style.display = 'block';
      }
      document.removeEventListener('face-landmarks:complete', handleComplete);
    };
    document.addEventListener('face-landmarks:complete', handleComplete);

    try {
      this.faceLandmarksController = await initFaceLandmarksCamera(container as HTMLElement);

      // Hide loading spinner once models are loaded
      if (loadingSpinner) {
        loadingSpinner.classList.add('hidden');
      }

      this.faceLandmarksController.start();
    } catch (error) {
      // Hide loading spinner on error
      if (loadingSpinner) {
        loadingSpinner.classList.add('hidden');
      }

      console.error('Face landmarks initialization failed:', error);
      const err = error as DOMException;
      let message = 'Camera access is required for skin analysis.';
      if (err.name === 'NotFoundError') {
        message = 'No camera found on this device.';
      } else if (err.name === 'NotAllowedError') {
        message = 'Camera access was denied. Please enable camera permissions.';
      }
      document.removeEventListener('face-landmarks:complete', handleComplete);
      this.showCameraErrorDialog(message);
    }
  }

  private showCameraErrorDialog(message: string): void {
    // Create dialog overlay (styles in components.css)
    const overlay = document.createElement('div');
    overlay.className = 'camera-error-overlay';
    overlay.innerHTML = `
      <div class="camera-error-dialog">
        <p class="camera-error-message">${message}</p>
        <div class="camera-error-buttons">
          <button class="btn btn-secondary" data-action="skip">Skip</button>
          <button class="btn btn-primary" data-action="retry">Try Again</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    // Handle button clicks
    overlay.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.dataset.action;

      if (action === 'retry') {
        overlay.remove();
        window.location.reload();
      } else if (action === 'skip') {
        overlay.remove();
        // Navigate to next screen (skip camera)
        this.cleanupCamera();
        document.body.classList.remove('camera-active');
        window.location.hash = '#analysis';
      }
    });
  }

  cleanupCamera() {
    if (this.faceLandmarksController) {
      this.faceLandmarksController.dispose();
      this.faceLandmarksController = null;
    }
  }

  displayAnalysisScreen() {
    // Display selfie on analysis screen
    const selfie = store.get('selfie');
    const selfiePreview = this.root.querySelector('#selfie-preview');

    if (selfiePreview && selfie) {
      // Create img element if it doesn't exist
      let img = selfiePreview.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.alt = 'Your selfie';
        // Insert before the overlay so overlay stays on top
        const overlay = selfiePreview.querySelector('.analysis-overlay');
        if (overlay) {
          selfiePreview.insertBefore(img, overlay);
        } else {
          selfiePreview.appendChild(img);
        }
      }
      img.src = selfie;
    }
  }

  displayResults() {
    // Display user selfie
    const selfie = store.get('selfie');
    const userSelfieImg = this.root.querySelector('#user-selfie');
    const potentialSelfieImg = this.root.querySelector('#potential-selfie');

    if (userSelfieImg && selfie) {
      userSelfieImg.src = selfie;
      // For demo, use the same image with a filter for "potential"
      potentialSelfieImg.src = selfie;
      potentialSelfieImg.style.filter = 'brightness(1.1) contrast(0.95)';
    }
  }

  async initializePaywall() {
    // Import paywall service and logger dynamically
    try {
      const [serviceModule, loggerModule] = await Promise.all([
        import('../domain/paywall-service.js'),
        import('../utils/logger.js')
      ]);

      const { paywallService } = serviceModule;
      const { paywallLogger } = loggerModule;

      paywallLogger.log('Initializing Paywall screen');

      // Get paywall ID from URL params if provided
      const urlParams = new URLSearchParams(window.location.search);
      const paywallId = urlParams.get('paywall');
      const viewId = urlParams.get('view');

      // Initialize paywall service
      await paywallService.initialize(paywallId || undefined);

      // Check for failed redirect and reopen checkout if needed
      paywallService.checkAndHandleFailedRedirect();

      // Switch to specific view if requested
      if (viewId) {
        paywallService.switchView(viewId);
      }

      // Get processed view data
      const viewData = paywallService.getProcessedView();
      if (!viewData) {
        paywallLogger.error('No paywall view data available');
        return;
      }

      // Update voucher card if present
      if (viewData.voucher) {
        const voucherCard = document.getElementById('v2-voucher-card');
        const voucherCode = document.getElementById('v2-voucher-code');
        const voucherDiscount = document.getElementById('v2-voucher-discount');

        if (voucherCard) {
          voucherCard.style.display = 'block';
          if (voucherCode) voucherCode.textContent = viewData.voucher.code;
          if (voucherDiscount) voucherDiscount.textContent = `${viewData.voucher.discount}% OFF`;
        }

        // Start voucher timer
        this.startVoucherTimer('v2-voucher-timer', 600); // 10 minutes
      }

      // Render pricing options (pass paywallService for option selection handling)
      this.renderV2PricingOptions(viewData, paywallService);

      // Render below section (express checkout, buttons, etc.)
      await this.renderV2BelowSection(viewData, paywallService);

      // Start countdown timer if needed
      const timerBanner = document.getElementById('v2-timer-banner');
      if (timerBanner) {
        timerBanner.style.display = 'block';
        this.startVoucherTimer('v2-countdown-timer', 600); // 10 minutes
      }

      // Listen for checkout events
      paywallService.on('checkout:success', (data: any) => {
        paywallLogger.log('Checkout successful, navigating to success', data);

        // Store email for success page
        if (data?.email) {
          const userData = Alpine.store('userData') as any;
          if (userData) {
            userData.email = data.email;
          }
        }

        window.location.hash = '#success';
      });

      paywallService.on('checkout:error', (error) => {
        paywallLogger.error('Checkout error', error);
        const errorDiv = document.getElementById('v2-payment-error');
        if (errorDiv) {
          errorDiv.textContent = error.message || 'Payment failed. Please try again.';
        }
      });

      paywallService.on('view:switched', () => {
        const updatedViewData = paywallService.getProcessedView();
        if (updatedViewData) {
          // Update voucher card
          const voucherCard = document.getElementById('v2-voucher-card');
          const voucherCode = document.getElementById('v2-voucher-code');
          const voucherDiscount = document.getElementById('v2-voucher-discount');

          if (updatedViewData.voucher) {
            if (voucherCard) voucherCard.style.display = 'block';
            if (voucherCode) voucherCode.textContent = updatedViewData.voucher.code;
            if (voucherDiscount) voucherDiscount.textContent = `${updatedViewData.voucher.discount}% OFF`;

            // Restart voucher timer
            this.startVoucherTimer('v2-voucher-timer', 600); // 15 minutes
          } else {
            if (voucherCard) voucherCard.style.display = 'none';
          }

          this.renderV2PricingOptions(updatedViewData, paywallService);
          this.renderV2BelowSection(updatedViewData, paywallService);
        }
      });

    } catch (error) {
      console.error('[ScreenController] Failed to initialize paywall:', error);
    }
  }

  startCountdownTimer(seconds) {
    // Cancel any existing timer
    if (this.countdownTimerId !== null) {
      clearTimeout(this.countdownTimerId);
      this.countdownTimerId = null;
    }

    const timerElement = this.root.querySelector('#countdown-timer');
    const voucherTimerElement = this.root.querySelector('#voucher-timer');
    const timerLabel = this.root.querySelector('.timer-label');
    const voucherExpires = this.root.querySelector('.voucher-expires');

    // Reset timer display state
    if (timerElement) {
      timerElement.style.display = '';
    }
    if (voucherTimerElement) {
      voucherTimerElement.style.display = '';
    }
    if (timerLabel) {
      timerLabel.textContent = "Your exclusive discount expires in:";
    }
    if (voucherExpires) {
      voucherExpires.textContent = "Promo code expires in:";
    }

    let remaining = seconds;

    const updateTimer = () => {
      const minutes = Math.floor(remaining / 60);
      const secs = remaining % 60;
      const timeText = `${minutes}:${secs.toString().padStart(2, '0')}`;

      if (remaining > 0) {
        if (timerElement) {
          timerElement.textContent = timeText;
        }
        if (voucherTimerElement) {
          voucherTimerElement.textContent = timeText;
        }
        remaining--;
        this.countdownTimerId = window.setTimeout(updateTimer, 1000);
      } else {
        // Timer expired - update UI
        if (timerLabel) {
          timerLabel.textContent = "No pressure, enjoy the discount";
        }
        if (voucherExpires) {
          voucherExpires.textContent = "No pressure, enjoy the discount";
        }
        // Hide the countdown timer element
        if (timerElement) {
          timerElement.style.display = 'none';
        }
        // Hide the voucher timer element
        if (voucherTimerElement) {
          voucherTimerElement.style.display = 'none';
        }

        this.countdownTimerId = null;
        document.dispatchEvent(new CustomEvent('timer:expired'));
      }
    };

    updateTimer();
  }

  openCheckout() {
    document.dispatchEvent(new CustomEvent('checkout:open'));
  }

  async resolveCheckoutEmail(): Promise<string | null> {
    let email = store.get('email');

    if (email && Email.isValid(email)) {
      return email;
    }

    email = await this.showEmailPopup();
    if (email && Email.isValid(email)) {
      return email;
    }

    return null;
  }

  updateVoucherCard(viewData) {
    const voucherCard = this.root.querySelector('.voucher-card');
    const voucherCode = this.root.querySelector('#voucher-code');
    const voucherDiscount = this.root.querySelector('#voucher-discount');
    const discountBadge = this.root.querySelector('#discount-badge');

    if (!viewData?.voucher) {
      if (voucherCard) {
        voucherCard.setAttribute('hidden', 'true');
      }
      return;
    }

    if (voucherCard) {
      voucherCard.removeAttribute('hidden');
    }
    if (voucherCode) {
      voucherCode.textContent = viewData.voucher.code;
    }
    if (voucherDiscount) {
      voucherDiscount.textContent = `${viewData.voucher.discountPercent}% OFF`;
    }
    if (discountBadge) {
      discountBadge.textContent = `SAVE ${viewData.voucher.discountPercent}% TODAY`;
    }
  }

  renderPricingOptions(viewData, isLoading = false) {
    const container = this.root.querySelector('#pricing-options');
    if (!container) return;

    // Get the pricing option template
    const template = document.getElementById('pricing-option-template') as HTMLTemplateElement;
    if (!template) {
      console.error('Pricing option template not found');
      return;
    }

    // Clear existing options
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // If loading, show placeholders
    if (isLoading) {
      // Show 3 loading placeholders
      for (let i = 0; i < 3; i++) {
        const placeholder = document.createElement('div');
        placeholder.className = 'pricing-option loading-placeholder';
        placeholder.innerHTML = `
          <div class="pricing-option-content">
            <div class="pricing-option-left">
              <div class="pricing-option-checkbox skeleton"></div>
              <div class="pricing-option-info">
                <div class="pricing-option-title skeleton-text"></div>
                <div class="pricing-option-prices">
                  <span class="pricing-crossed skeleton-text"></span>
                  <span class="pricing-current skeleton-text"></span>
                </div>
              </div>
            </div>
            <div class="pricing-option-right">
              <div class="pricing-daily-amount skeleton-text"></div>
              <div class="pricing-daily-label skeleton-text"></div>
            </div>
          </div>
        `;
        container.appendChild(placeholder);
      }
      return;
    }

    // Render each option
    let selectedIndex = 0; // Track which option should be selected

    // Find option with badge, if any
    const badgeIndex = viewData.options.findIndex(opt => opt.badge);
    if (badgeIndex !== -1) {
      selectedIndex = badgeIndex;
    }

    viewData.options.forEach((option, index) => {
      // Clone the template
      const optionEl = template.content.cloneNode(true) as DocumentFragment;
      const optionDiv = optionEl.querySelector('.pricing-option') as HTMLElement;

      if (!optionDiv) return;

      // Set the option ID
      optionDiv.dataset.optionId = option.priceId;

      // Select the determined option
      if (index === selectedIndex) {
        optionDiv.classList.add('selected');
      }

      // Set the badge if present
      const badgeEl = optionDiv.querySelector('.pricing-option-badge') as HTMLElement;
      if (option.badge && badgeEl) {
        badgeEl.textContent = option.badge;
        badgeEl.style.display = '';
      }

      // Set the title
      const titleEl = optionDiv.querySelector('.pricing-option-title');
      if (titleEl) {
        titleEl.textContent = option.title;
      }

      // Set the prices
      const crossedPriceEl = optionDiv.querySelector('.pricing-crossed');
      if (crossedPriceEl) {
        crossedPriceEl.textContent = option.crossedPrice.formatted;
      }

      const currentPriceEl = optionDiv.querySelector('.pricing-current');
      if (currentPriceEl) {
        currentPriceEl.textContent = option.uncrossedPrice.formatted;
      }

      // Set the daily price
      const dailyPriceEl = optionDiv.querySelector('.pricing-daily-amount');
      if (dailyPriceEl) {
        dailyPriceEl.textContent = option.dailyPrice.formatted;
      }

      container.appendChild(optionEl);
    });
  }

  setupOptionSelection(paywallService) {
    const container = this.root.querySelector('#pricing-options');
    if (!container) return;

    container.addEventListener('click', (e) => {
      const option = e.target.closest('.pricing-option');
      if (!option) return;

      // Remove selected from all options
      container.querySelectorAll('.pricing-option').forEach(opt => {
        opt.classList.remove('selected');
      });

      // Add selected to clicked option
      option.classList.add('selected');

      // Update service
      const optionId = option.dataset.optionId;
      paywallService.selectOption(optionId);

      const updatedView = paywallService.getProcessedView();
      if (updatedView) {
        this.renderBelowSection(updatedView, paywallService);
      }
    });

    // Select default option
    const firstSelected = container.querySelector('.pricing-option.selected');
    if (firstSelected) {
      paywallService.selectOption(firstSelected.dataset.optionId);
    }
  }

  updateButtonState(isLoading) {
    const ctaButton = this.root.querySelector('#paywall-cta-button');
    if (!ctaButton) return;

    if (isLoading) {
      ctaButton.disabled = true;
      ctaButton.classList.add('loading');
      ctaButton.innerHTML = '<span class="loading-spinner"></span> Loading prices...';
    } else {
      ctaButton.disabled = false;
      ctaButton.classList.remove('loading');
      ctaButton.textContent = 'Get My Plan';
    }
  }

  renderBelowSection(viewData, paywallService) {
    const container = this.root.querySelector('#paywall-below-section');
    if (!container || !viewData) return;

    // Clear existing content
    container.innerHTML = '';

    // Render each item in the below section
    viewData.belowItems.forEach(item => {
      if (item.type === 'button') {
        this.renderBelowButton(container, item, paywallService);
      } else if (item.type === 'checkout') {
        this.renderInlineCheckout(container, paywallService);
      } else if (item.type === 'express-checkout') {
        this.renderExpressCheckout(container, paywallService);
      }
    });
  }

  renderBelowButton(container, buttonConfig, paywallService) {
    const button = document.createElement('button');
    button.className = 'btn-primary large glow';
    button.textContent = buttonConfig.text || 'Get My Plan';
    button.dataset.action = buttonConfig.action || 'open-checkout';

    button.addEventListener('click', async () => {
      if (button.disabled) return;

      try {
        const email = await this.resolveCheckoutEmail();
        if (!email) return;

        const mode = buttonConfig.action === 'open-checkout-popup' ? 'popup' : undefined;
        await paywallService.openCheckout(email, undefined, undefined, { mode });
      } catch (error) {
        console.error('[ScreenController] Failed to open checkout:', error);
      }
    });

    container.appendChild(button);
  }

  renderInlineCheckout(container, paywallService) {
    const inlineContainer = document.createElement('div');
    inlineContainer.className = 'inline-checkout-widget';
    inlineContainer.id = `inline-checkout-container-${Date.now()}`;

    const placeholder = document.createElement('p');
    placeholder.className = 'checkout-placeholder';
    placeholder.textContent = 'Tap to load secure checkout';
    inlineContainer.appendChild(placeholder);

    container.appendChild(inlineContainer);

    let isMounting = false;
    let hasMounted = false;

    const mountInlineCheckout = async () => {
      if (hasMounted || isMounting) return;
      isMounting = true;
      placeholder.textContent = 'Preparing checkout...';

      try {
        const email = await this.resolveCheckoutEmail();
        if (!email) {
          placeholder.textContent = 'Tap to load secure checkout';
          return;
        }

        await paywallService.openCheckout(
          email,
          undefined,
          undefined,
          { mode: 'inline', inlineContainer }
        );

        hasMounted = true;
      } catch (error) {
        console.error('[ScreenController] Failed to open inline checkout:', error);
        placeholder.textContent = 'Checkout unavailable. Please try another method.';
        placeholder.classList.add('error');
      } finally {
        isMounting = false;
        if (!hasMounted && !placeholder.classList.contains('error')) {
          placeholder.textContent = 'Tap to load secure checkout';
        }
      }
    };

    const handleInlineInteraction = async () => {
      inlineContainer.removeEventListener('pointerdown', handleInlineInteraction);
      inlineContainer.removeEventListener('focusin', handleInlineInteraction);
      await mountInlineCheckout();
      if (!hasMounted && !placeholder.classList.contains('error')) {
        inlineContainer.addEventListener('pointerdown', handleInlineInteraction, { once: true });
        inlineContainer.addEventListener('focusin', handleInlineInteraction, { once: true });
      }
    };

    inlineContainer.addEventListener('pointerdown', handleInlineInteraction, { once: true });
    inlineContainer.addEventListener('focusin', handleInlineInteraction, { once: true });
  }

  async renderExpressCheckout(container, paywallService) {
    const expressContainer = document.createElement('div');
    expressContainer.className = 'express-checkout-container';

    // Check availability of express checkout methods
    try {
      const availability = await paywallService.getExpressCheckoutAvailability();

      if (availability.applePay || availability.googlePay) {
        const label = document.createElement('div');
        label.className = 'express-checkout-label';
        label.textContent = 'Express checkout';
        expressContainer.appendChild(label);

        // Create container for Stripe Express Checkout Element
        const elementContainer = document.createElement('div');
        elementContainer.className = 'express-checkout-element';
        elementContainer.id = `express-checkout-${Date.now()}`;
        expressContainer.appendChild(elementContainer);

        container.appendChild(expressContainer);

        try {
          const email = await this.resolveCheckoutEmail();
          if (!email) {
            expressContainer.remove();
            return;
          }

          const selectedPriceId = container.closest('[data-screen]')?.dataset.selectedOption;
          await paywallService.mountExpressCheckout(elementContainer.id, email, selectedPriceId);
        } catch (error) {
          console.error('[ScreenController] Failed to mount express checkout:', error);
          const errorMsg = document.createElement('p');
          errorMsg.className = 'express-checkout-error';
          errorMsg.textContent = 'Express checkout unavailable';
          expressContainer.appendChild(errorMsg);
        }
      }
    } catch (error) {
      console.error('[ScreenController] Failed to check express checkout availability:', error);
    }
  }

  showEmailPopup() {
    return new Promise((resolve) => {
      // Get the email popup template
      const template = document.getElementById('email-popup-template');
      if (!template) {
        console.error('Email popup template not found');
        resolve(null);
        return;
      }

      // Clone and append the popup to the body
      const popupContent = template.content.cloneNode(true);
      document.body.appendChild(popupContent);

      // Get references to popup elements
      const backdrop = document.getElementById('email-modal-backdrop');
      const closeButton = document.getElementById('email-modal-close');
      const form = document.getElementById('email-popup-form');
      const emailInput = document.getElementById('popup-email-input');
      const submitButton = document.getElementById('popup-email-submit');
      const validation = document.getElementById('popup-email-validation');

      if (!backdrop || !form || !emailInput || !submitButton) {
        console.error('Email popup elements not found');
        resolve(null);
        return;
      }

      // Function to close the popup
      const closePopup = (email = null) => {
        if (backdrop && backdrop.parentNode) {
          backdrop.parentNode.removeChild(backdrop);
        }
        resolve(email);
      };

      // Close button handler
      if (closeButton) {
        closeButton.addEventListener('click', () => closePopup());
      }

      // Click outside to close
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          closePopup();
        }
      });

      // Email validation on input
      emailInput.addEventListener('input', () => {
        const value = emailInput.value.trim();

        // Clear previous validation
        if (validation) {
          validation.textContent = '';
          validation.classList.remove('error');
        }

        if (value && !Email.isValid(value)) {
          submitButton.disabled = true;
          submitButton.classList.add('btn-disabled');
          if (validation) {
            validation.textContent = 'Please enter a valid email address';
            validation.classList.add('error');
          }
        } else if (value) {
          submitButton.disabled = false;
          submitButton.classList.remove('btn-disabled');
        } else {
          submitButton.disabled = true;
          submitButton.classList.add('btn-disabled');
        }
      });

      // Initialize button state
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');

      // Form submission
      form.addEventListener('submit', (e) => {
        e.preventDefault();

        const email = emailInput.value.trim().toLowerCase();

        if (Email.isValid(email)) {
          // Store the email
          store.set('email', email);

          // Close popup and return email
          closePopup(email);
        } else {
          if (validation) {
            validation.textContent = 'Please enter a valid email address';
            validation.classList.add('error');
          }
        }
      });

      // Focus on the input field
      emailInput.focus();
    });
  }

  downloadApp() {
    // iOS app store link
    window.location.href = 'https://apps.apple.com/app/lunai-care/id123456789'; // Replace with actual app ID
  }

  showSuccessAnimation() {
    // Create confetti effect
    const confettiContainer = this.root.querySelector('#confetti');
    if (confettiContainer) {
      for (let i = 0; i < 50; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti-piece';
        confetti.style.left = `${Math.random() * 100}%`;
        confetti.style.animationDelay = `${Math.random() * 3}s`;
        confetti.style.backgroundColor = ['#C33C54', '#FFB140', '#3F88C5'][Math.floor(Math.random() * 3)];
        confettiContainer.appendChild(confetti);
      }
    }

    // Display user email
    const emailDisplay = this.root.querySelector('#user-email-display');
    const userEmail = store.get('email');
    if (emailDisplay && userEmail) {
      emailDisplay.textContent = userEmail;
    }
  }

  getCurrentScreen() {
    console.log('getCurrentScreen returned:', this.currentScreen);
    return this.currentScreen;
  }

  initializePredictionAnimations() {
    // Animate metric values with counter effect
    const animateCounter = (element, start, end, duration) => {
      const startTime = performance.now();
      const difference = end - start;

      const updateCounter = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-out animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.round(start + difference * easeOutQuart);

        element.textContent = `${currentValue}%`;

        if (progress < 1) {
          requestAnimationFrame(updateCounter);
        }
      };

      requestAnimationFrame(updateCounter);
    };

    // Animate improvement values
    const animateImprovement = (element, improvement, duration) => {
      const startTime = performance.now();

      const updateImprovement = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.round(improvement * easeOutQuart);

        element.textContent = `(+${currentValue}%)`;

        if (progress < 1) {
          requestAnimationFrame(updateImprovement);
        }
      };

      requestAnimationFrame(updateImprovement);
    };

    // Animate chart bars with dynamic values
    const chartItems = this.root.querySelectorAll('.chart-item');
    chartItems.forEach((item, index) => {
      const currentBar = item.querySelector('.bar-current');
      const targetBar = item.querySelector('.bar-target');
      const currentValueEl = item.querySelector('.current-value');
      const targetValueEl = item.querySelector('.target-value');

      if (currentBar && targetBar) {
        const currentVal = parseInt(currentBar.dataset.current || '0');
        const targetVal = parseInt(targetBar.dataset.target || '100');

        // Set initial animation delays based on index
        currentBar.style.animationDelay = `${0.3 + index * 0.1}s`;
        targetBar.style.animationDelay = `${1 + index * 0.1}s`;

        // Animate the number counters
        setTimeout(() => {
          if (currentValueEl) {
            animateCounter(currentValueEl, 0, currentVal, 1500);
          }
        }, 300 + index * 100);

        setTimeout(() => {
          if (targetValueEl) {
            animateCounter(targetValueEl, currentVal, targetVal, 1500);
          }
        }, 1000 + index * 100);
      }
    });

    // Animate metric values in comparison columns (if they exist)
    const improvedValues = this.root.querySelectorAll('.metric-value.improved');
    improvedValues.forEach((element, index) => {
      const text = element.textContent;
      const match = text?.match(/\+(\d+)%/);

      if (match) {
        const improvement = parseInt(match[1]);
        const baseValue = parseInt(text.match(/(\d+)%/)?.[1] || '0');

        // Temporarily set to base value
        element.textContent = `${baseValue}%`;

        // After a delay, show the improvement animation
        setTimeout(() => {
          const improvementSpan = document.createElement('span');
          improvementSpan.style.marginLeft = '5px';
          improvementSpan.style.color = 'var(--color-success)';
          element.appendChild(improvementSpan);
          animateImprovement(improvementSpan, improvement, 1500);
        }, 1500 + index * 200);
      }
    });

    // Animate the user count in confidence badge
    const confidenceBadge = this.root.querySelector('.confidence-badge');
    if (confidenceBadge) {
      const text = confidenceBadge.textContent;
      const match = text?.match(/(\d+,?\d+)/);

      if (match) {
        const targetNumber = parseInt(match[1].replace(',', ''));
        const formattedNumber = targetNumber.toLocaleString();

        // Create animated counter
        const animateUserCount = () => {
          const duration = 2000;
          const startTime = performance.now();

          const updateCount = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = Math.round(targetNumber * easeOutQuart);

            confidenceBadge.innerHTML = text.replace(/\d+,?\d+/, currentValue.toLocaleString());

            if (progress < 1) {
              requestAnimationFrame(updateCount);
            }
          };

          requestAnimationFrame(updateCount);
        };

        // Start animation after a delay
        setTimeout(animateUserCount, 2000);
      }
    }

    // Add sparkle effects to achievement banner
    const achievementBanner = this.root.querySelector('.achievement-banner');
    if (achievementBanner) {
      // Create sparkle elements
      const createSparkle = () => {
        const sparkle = document.createElement('span');
        sparkle.style.position = 'absolute';
        sparkle.style.fontSize = '20px';
        sparkle.textContent = '✨';
        sparkle.style.left = `${Math.random() * 100}%`;
        sparkle.style.top = `${Math.random() * 100}%`;
        sparkle.style.animation = 'sparkle 2s ease-in-out forwards';
        sparkle.style.pointerEvents = 'none';

        achievementBanner.style.position = 'relative';
        achievementBanner.appendChild(sparkle);

        // Remove sparkle after animation
        setTimeout(() => sparkle.remove(), 2000);
      };

      // Add sparkles periodically
      setInterval(createSparkle, 3000);

      // Initial sparkles
      setTimeout(createSparkle, 500);
      setTimeout(createSparkle, 1000);
      setTimeout(createSparkle, 1500);
    }
  }


  renderV2PricingOptions(viewData, paywallService) {
    const container = document.getElementById('v2-pricing-options');
    if (!container) return;

    // Clear existing options
    container.innerHTML = '';

    // Find the default option (first with badge, or first option)
    const defaultOptionIndex = viewData.processed_options.findIndex(opt => opt.badge) !== -1
      ? viewData.processed_options.findIndex(opt => opt.badge)
      : 0;

    viewData.processed_options.forEach((option, index) => {
      const optionDiv = document.createElement('div');
      optionDiv.className = 'pricing-option';
      optionDiv.dataset.optionId = option.price;

      // Add badge if present
      if (option.badge) {
        const badge = document.createElement('div');
        badge.className = 'pricing-option-badge';
        badge.textContent = option.badge;
        optionDiv.appendChild(badge);
      }

      // Create option content
      const content = document.createElement('div');
      content.className = 'pricing-option-content';

      // Left side with checkbox and info
      const left = document.createElement('div');
      left.className = 'pricing-option-left';

      const checkbox = document.createElement('div');
      checkbox.className = 'pricing-option-checkbox';

      // Select the default option (badge option or first)
      if (index === defaultOptionIndex) {
        checkbox.classList.add('checked');
        // Store the default selection
        container.closest('[data-screen]').dataset.selectedOption = option.price;
      }

      const info = document.createElement('div');
      info.className = 'pricing-option-info';

      const title = document.createElement('div');
      title.className = 'pricing-option-title';
      title.textContent = option.title;

      const prices = document.createElement('div');
      prices.className = 'pricing-option-prices';

      if (option.processed_prices.crossed) {
        const crossed = document.createElement('span');
        crossed.className = 'pricing-crossed';
        crossed.textContent = `$${formatPrice(option.processed_prices.crossed)}`;
        prices.appendChild(crossed);
      }

      const current = document.createElement('span');
      current.className = 'pricing-current';
      const displayPrice = option.processed_prices.pay_now || option.processed_prices.recurring;
      current.textContent = `$${formatPrice(displayPrice)}`;
      if (option.price_postfix) {
        current.textContent += ` ${option.price_postfix}`;
      }
      prices.appendChild(current);

      info.appendChild(title);
      info.appendChild(prices);

      left.appendChild(checkbox);
      left.appendChild(info);

      // Right side with daily price
      const right = document.createElement('div');
      right.className = 'pricing-option-right';

      if (option.processed_prices.daily) {
        const dailyAmount = document.createElement('div');
        dailyAmount.className = 'pricing-daily-amount';
        dailyAmount.textContent = `$${formatPrice(option.processed_prices.daily)}`;

        const dailyLabel = document.createElement('div');
        dailyLabel.className = 'pricing-daily-label';
        dailyLabel.textContent = 'per day';

        right.appendChild(dailyAmount);
        right.appendChild(dailyLabel);
      }

      content.appendChild(left);
      content.appendChild(right);
      optionDiv.appendChild(content);

      // Add click handler
      optionDiv.addEventListener('click', async () => {
        // Remove checked from all
        container.querySelectorAll('.pricing-option-checkbox').forEach(cb => {
          cb.classList.remove('checked');
        });
        // Add checked to this one
        checkbox.classList.add('checked');

        // Store selected option
        optionDiv.closest('[data-screen]').dataset.selectedOption = option.price;

        // Update Express Checkout and Payment Element if they exist
        const expressContainer = document.getElementById('express-checkout-container');
        if (expressContainer && expressContainer.style.display !== 'none' && paywallService) {
          try {
            const email = store.get('email');
            // Clear existing Express Checkout
            expressContainer.innerHTML = '';
            // Remount with new price
            await paywallService.mountExpressCheckout(
              expressContainer,
              email,
              option.price
            );

            // Also update the Stripe amount if Payment Element exists
            const amount = Math.round((option.processed_prices.pay_now ||
              option.processed_prices.recurring || 0) * 100);
            if (paywallService.stripeService) {
              await paywallService.stripeService.updateAmount(amount);
            }
          } catch (error) {
            console.error('Failed to update Express Checkout:', error);
          }
        }
      });

      container.appendChild(optionDiv);
    });

    // No need to click - default option is already selected and stored in dataset
  }

  async renderV2BelowSection(viewData, paywallService) {
    const container = document.getElementById('v2-paywall-below-section');
    if (!container) return;

    const buttonsContainer = document.getElementById('v2-buttons-container');
    const expressContainer = document.getElementById('v2-express-checkout-container');
    const standardContainer = document.getElementById('v2-standard-checkout-container');

    // Clear buttons container
    if (buttonsContainer) buttonsContainer.innerHTML = '';

    // Track if we need to use alternative items
    let useAlternativeItems = false;

    // First pass: check if express checkout is empty
    for (const item of viewData.below.items) {
      if (item.type === 'express-checkout') {
        if (expressContainer) {
          expressContainer.style.display = 'block';
          const email = store.get('email');
          const selectedPriceId = container.closest('[data-screen]')?.dataset.selectedOption;
          const hasExpressCheckout = await paywallService.mountExpressCheckout(
            expressContainer,
            email,
            selectedPriceId
          );

          if (!hasExpressCheckout) {
            // No express payment methods available
            expressContainer.style.display = 'none';
            console.log('No express checkout methods available, will use alternative items');
            useAlternativeItems = true;
          }
        }
      }
    }

    // Determine which items to render
    const itemsToRender = useAlternativeItems && viewData.below.alternative_items
      ? viewData.below.alternative_items
      : viewData.below.items;

    // Render the items
    for (const item of itemsToRender) {
      if (item.type === 'button') {
        // Create button
        const button = document.createElement('button');
        let buttonStyle = 'btn-primary';
        if (item.style === 'secondary') {
          buttonStyle = 'btn-secondary';
        } else if (item.style === 'link') {
          buttonStyle = 'btn-link';
        }
        button.className = `${buttonStyle} btn-full`;
        button.textContent = item.text;

        button.addEventListener('click', async () => {
          const selectedOption = container.closest('[data-screen]').dataset.selectedOption;
          if (!selectedOption) {
            console.error('No option selected');
            return;
          }

          const email = store.get('email');

          if (item.action === 'open-checkout-popup') {
            await paywallService.openCheckout({
              priceId: selectedOption,
              email: email
            });
          } else {
            console.log('Unknown button action:', item.action);
          }
        });

        if (buttonsContainer) buttonsContainer.appendChild(button);

      } else if (item.type === 'express-checkout') {
        // Already handled in first pass

      } else if (item.type === 'checkout') {
        // Mount Standard Checkout (Payment Element) - without client secret initially
        if (standardContainer) {
          standardContainer.style.display = 'block';
          const paymentElement = document.getElementById('v2-payment-element');
          const submitBtn = document.getElementById('v2-submit-payment');
          const email = store.get('email');

          if (paymentElement) {
            // Mount Payment Element without creating subscription
            await paywallService.mountStandardCheckout(paymentElement, email);

            // Setup submit button - this is where subscription is created
            if (submitBtn) {
              submitBtn.addEventListener('click', async () => {
                const selectedOption = container.closest('[data-screen]').dataset.selectedOption;
                if (!selectedOption) {
                  const errorDiv = document.getElementById('v2-payment-error');
                  if (errorDiv) errorDiv.textContent = 'Please select a pricing plan';
                  return;
                }

                submitBtn.disabled = true;
                submitBtn.textContent = 'Processing...';

                const errorDiv = document.getElementById('v2-payment-error');
                if (errorDiv) errorDiv.textContent = '';

                try {
                  // NOW create subscription and complete payment
                  const result = await paywallService.completeCheckout(
                    selectedOption,
                    email || ''
                  );

                  if (result.success) {
                    window.location.hash = '#success';
                  } else {
                    if (errorDiv) {
                      errorDiv.textContent = result.error?.message || 'Payment failed';
                    }
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Complete Payment';
                  }
                } catch (error) {
                  console.error('Checkout error:', error);
                  if (errorDiv) {
                    errorDiv.textContent = error.message || 'Payment failed';
                  }
                  submitBtn.disabled = false;
                  submitBtn.textContent = 'Complete Payment';
                }
              });
            }
          }
        }
      }
    }
  }

  startVoucherTimer(elementId, duration) {
    const element = document.getElementById(elementId);
    if (!element) return;

    // Clear existing timer for this element if any
    if (this.voucherTimerIds.has(elementId)) {
      clearTimeout(this.voucherTimerIds.get(elementId));
      this.voucherTimerIds.delete(elementId);
    }

    let remainingTime = duration;

    const updateTimer = () => {
      if (remainingTime <= 0) {
        element.textContent = '00:00';
        this.voucherTimerIds.delete(elementId);
        return;
      }

      const minutes = Math.floor(remainingTime / 60);
      const seconds = remainingTime % 60;
      element.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      remainingTime--;
      const timerId = setTimeout(updateTimer, 1000);
      this.voucherTimerIds.set(elementId, timerId);
    };

    updateTimer();
  }

  initializeCoinPiles() {
    // Clean up existing instance if any
    if (this.coinPiles) {
      this.coinPiles.dispose();
      this.coinPiles = null;
    }

    // Wait for DOM to update
    setTimeout(() => {
      const container = this.root.querySelector('#coin-piles-container');
      if (container) {
        this.coinPiles = new CoinPiles(container as HTMLElement);
      } else {
        console.error('Coin piles container not found');
      }
    }, 100);
    // Animate the info-big-text with typing effect
    const infoBigText = this.root.querySelector('.info-big-text');
    if (infoBigText) {
      this.animateTypingText(infoBigText as HTMLElement, 5000);
    }
  }

  animateTypingText(element: HTMLElement, delay: number) {
    const originalText = element.textContent || '';

    // Hide text initially
    element.textContent = '';

    setTimeout(() => {
      // Show element and clear text
      element.textContent = '';

      let currentIndex = 0;

      const typeNextChar = () => {
        if (currentIndex < originalText.length) {
          element.textContent += originalText[currentIndex];
          currentIndex++;
          setTimeout(typeNextChar, 50); // 50ms between each character
        }
      };

      typeNextChar();
    }, delay);
  }
}
